<template>
  <BaseModal
    :is-open="isOpen"
    title="계좌비밀번호"
    :show-default-footer="false"
    :close-on-overlay-click="false"
    :close-on-escape="true"
    content-padding="none"
    @close="handleClose"
    @update:is-open="$emit('update:isOpen', $event)"
  >
    <div class="px-padding-36 flex flex-col justify-center py-10">
      <div class="gap-size-12 flex flex-col">
        <p class="text-font-18 font-medium">계좌 비밀번호 입력</p>
        <p class="text-font-14">안전한 거래를 위해 비밀번호 확인이 필요해요</p>
      </div>

      <div class="gap-size-4 mt-size-36 flex flex-col">
        <p class="text-font-13 font-medium">계좌번호</p>
        <BaseInputSelect
          size="md"
          :teleport="false"
          :modelValue="accountStore.selectedAccountNo"
          :options="accountList"
          @open="getAccountList"
          @update:modelValue="handleAccountChange"
        />
      </div>

      <div v-if="isAgree" class="gap-size-12 mt-size-36 flex flex-col">
        <BasePIN :pin="password" @key-press="handleKeyPress" :key="accountPasswordKey">
          <template v-if="passwordFailedCount > 0" #content>
            <p class="my-size-8 text-font-14 text-[var(--input-color-text-error)]">
              비밀번호가 일치하지 않습니다 ({{ passwordFailedCount }} / 10)
            </p>
            <p
              v-if="isDemoMode()"
              class="mt-size-4 text-font-14 text-[var(--input-color-text-error)]"
            >
              모의계좌 비밀번호는 "000000" 입니다
            </p>
            <a
              v-if="!isDemoMode()"
              class="mb-size-24 text-font-14 cursor-pointer text-[var(--button-tab-menu-text-off)] underline"
              @click="openPasswordResetDialog"
            >
              계좌 비밀번호를 잊으셨나요?
            </a>
          </template>
        </BasePIN>
      </div>

      <button
        class="p-padding-16 bg-bg-bg-innerframe mt-size-12 border-bg-bg-outline rounded-[10px] border border-2"
        :class="{ 'bg-primary-primary050 border-bg-primary': isAgree }"
        @click="isAgree = !isAgree"
      >
        <div class="gap-size-8 flex items-center">
          <div
            v-if="isAgree"
            class="w-size-20 h-size-20 bg-bg-primary flex items-center justify-center overflow-hidden rounded-full"
          >
            <BaseIcon name="check-sm" color="white" />
          </div>
          <div
            v-else
            class="w-size-20 h-size-20 bg-bg-bg-default border-bg-bg-outline rounded-full border border-[1.5px]"
          ></div>
          <p class="text-font-16 text-default font-bold">빠른 거래</p>
          <BaseChip
            class="bg-bg-primary text-default text-font-12 font-medium"
            label="추천"
            size="sm"
          />
        </div>
        <p class="text-font-13 ml-[30px] text-left">한 번만 입력하면 안전하게 거래할 수 있어요</p>
        <ul class="text-default-muted-dark text-font-12 ml-[46px] list-disc text-left">
          <li class="">계좌비밀번호 재입력 없이 거래 가능</li>
          <li>선택하지 않을 경우, 주문시마다 계좌비밀번호 입력 필요</li>
        </ul>
      </button>
    </div>
    <!-- 푸터 영역 -->
    <template #footer>
      <div class="flex items-center justify-center gap-3">
        <BaseButton
          variant="contained"
          color="primary"
          label="확인"
          :isLoading="isSubmitting"
          :disabled="isConfirmDisabled || passwordFailedCount >= 10 || isSubmitting"
          @click="handleAgree"
          fullWidth
        />
        <BaseButton
          variant="outlined"
          color="white"
          label="취소"
          :disabled="isSubmitting"
          @click="handleClose"
          fullWidth
        />
      </div>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { BaseModal, BaseButton, BaseInputSelect, BasePIN, BaseIcon, BaseChip } from '@template/ui';
import { useGlobalDialogManager } from '@/composables/useGlobalDialogManager';
import { useAccountStore, useAssetStore } from '@template/stores';
import { computed, onMounted, ref, watch } from 'vue';
import { accountService } from '@/services/api';
import { isDemoMode } from '@template/utils';

const isOpen = defineModel<boolean>('isOpen', { required: true });
const isAgree = ref(true);
const password = ref<string[]>([]);
const isLoadingAccounts = ref(false);
const isSubmitting = ref(false);
interface Emits {
  (e: 'close'): void;
  (e: 'confirm', isAgree: boolean, password: string): void;
  (e: 'update:isOpen', value: boolean): void;
}

const accountPasswordKey = ref(0);
const accountStore = useAccountStore();
const globalDialogManager = useGlobalDialogManager();
const assetStore = useAssetStore();

const passwordFailedCount = computed(() => {
  return accountStore.accountPasswordFailedCount;
});

const emit = defineEmits<Emits>();

const accountList = computed(() =>
  [...accountStore.availableAccountListSortedComputed].map((account) => ({
    value: account.accountNo,
    label: `${account.accountAlias}#${account.accountSequence} ${account.accountNo}`,
  }))
);

const isConfirmDisabled = computed(() => {
  if (isAgree.value) {
    return password.value.length !== 6;
  } else {
    return false;
  }
});

const handleClose = () => {
  password.value = [];
  isAgree.value = false;
  isSubmitting.value = false;
  emit('close');
  emit('update:isOpen', false);
};
const handleAgree = () => {
  if (isConfirmDisabled.value) return;
  isSubmitting.value = true;
  const passwordValue = password.value.join('');
  const isAgreeValue = isAgree.value;
  emit('confirm', isAgreeValue, passwordValue);
};

/**
 * 계좌 변경 핸들러
 * selectAccount가 완료될 때까지 기다립니다 (동기적으로 수행)
 */
const handleAccountChange = async (accountNo: string | undefined) => {
  try {
    await accountStore.selectAccount(accountNo);
  } catch (error) {
    console.error('계좌 변경 실패:', error);
  }
};

/**
 * 계좌 목록을 조회합니다.
 */
const getAccountList = async () => {
  // 기존 데이터가 있으면 로딩 상태를 표시하지 않음
  if (accountStore.accountListComputed.length === 0) {
    isLoadingAccounts.value = true;
  }

  try {
    const result = await accountService.getAccountInfo();
    const accountList = result.data?.accountList || [];

    // StatusBarArea에서는 계좌 목록만 업데이트
    await accountStore.setAccountList(accountList);

    // 선택된 계좌가 있으면 해당 계좌의 출금가능금액으로 예수금 초기화
    if (accountStore.selectedAccountNo && accountList.length > 0) {
      const selectedAccount = accountList.find(
        (account) => account.accountNo === accountStore.selectedAccountNo
      );
      if (selectedAccount?.availableWithdrawAmount) {
        const depositAmount = parseFloat(selectedAccount.availableWithdrawAmount);
        assetStore.setDepositBalance(depositAmount); // 예수금만 설정, 기존 증거금 유지
      }
    }
  } catch (error) {
    console.error('계좌 목록 조회 실패:', error);
  } finally {
    isLoadingAccounts.value = false;
  }
};

const KEYPAD_SPECIAL_KEYS = {
  BACKSPACE: 'BACKSPACE',
  EMPTY: 'EMPTY',
} as const;

const handleKeyPress = (key: string) => {
  if (key === KEYPAD_SPECIAL_KEYS.BACKSPACE) {
    password.value = password.value.slice(0, -1);
  } else {
    password.value.push(key);
  }
};

const openPasswordResetDialog = () => {
  globalDialogManager.open('accountPasswordResetConfirm');
};

watch([passwordFailedCount, isAgree], (newVal) => {
  password.value = [];
  isSubmitting.value = false;
  accountPasswordKey.value++;
});

onMounted(() => {
  accountStore.resetAccountPasswordFailedCount();
});
</script>
